import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, X, Send, CheckCircle, AlertCircle, Loader2, Bell } from 'lucide-react';
import { PROXY_API_PATH } from '../../constants';
import { readSiteFeatures, isFeatureEnabled } from '../../utils/siteFeatures';
import { loadBranded } from '../../utils/bootstrap';
import './TopInfoBar.css';

// Server-injected contact details (window.__HM_BRAND__.contact) take
// priority — eliminates the "+1 800 000 0000" placeholder flash before the
// API fetch lands. Falls back to localStorage cache, then DEFAULTS.
const load = (key, fallback) => {
    if (key === 'hm_page_contact') return loadBranded(key, 'contact', fallback);
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};

// No sample number or address: a site that hasn't entered its own simply
// doesn't show that item (a fake "+1 800 000 0000" reached nobody).
const DEFAULTS = {
    phone: '',
    email: '',
};

const BAR_HEIGHT = 30; // px — must match CSS height

const TopInfoBar = () => {
    const { t } = useTranslation();
    const [contact, setContact]     = useState(() => load('hm_page_contact', DEFAULTS));
    const [features, setFeatures]   = useState(readSiteFeatures);
    // Toggled from Page Management → Home → Custom (src/pages/Private/sections/
    // PagesManagementSection.js's TopInfoBarEditor) — site-wide, not Home-page-
    // scoped, it just lives there for admin discoverability. Defaults to shown
    // so existing sites keep their current behaviour unless an admin turns it off.
    const [enabled, setEnabled]     = useState(() => load('hm_top_info_bar', { enabled: true }).enabled !== false);
    const [showModal, setShowModal] = useState(false);
    const [email, setEmail]         = useState('');
    const [status, setStatus]       = useState('idle'); // idle | loading | success | duplicate | error

    const barRef = useRef(null);

    /* ── Sync contact info when admin saves settings ── */
    useEffect(() => {
        const apply = () => {
            setContact(load('hm_page_contact', DEFAULTS));
            setFeatures(readSiteFeatures());
            setEnabled(load('hm_top_info_bar', { enabled: true }).enabled !== false);
        };
        window.addEventListener('hm-settings-loaded', apply);
        return () => window.removeEventListener('hm-settings-loaded', apply);
    }, []);

    const newsletterEnabled = isFeatureEnabled(features, 'newsletter');

    const closeModal = useCallback(() => {
        setShowModal(false);
        setEmail('');
        setStatus('idle');
    }, []);

    const openModal = useCallback(() => {
        setStatus('idle');
        setEmail('');
        setShowModal(true);
    }, []);

    useEffect(() => {
        if (!newsletterEnabled && showModal) {
            setShowModal(false);
            setEmail('');
            setStatus('idle');
        }
    }, [newsletterEnabled, showModal]);

    /* ── Publish bar height as CSS variable ── */
    // Use a ref to cache height so visibility changes never force a reflow.
    const cachedHeightRef = useRef(BAR_HEIGHT);

    const applyHeight = useCallback((h) => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
            document.documentElement.style.setProperty('--tib-height', '0px');
            return;
        }
        const px = h ?? cachedHeightRef.current;
        document.documentElement.style.setProperty('--tib-height', `${px}px`);
    }, []);

    /* ResizeObserver fires AFTER layout is done — no forced reflow */
    useEffect(() => {
        if (!barRef.current) return;
        const ro = new ResizeObserver((entries) => {
            const h = entries[0]?.contentRect.height || BAR_HEIGHT;
            cachedHeightRef.current = h;
            applyHeight(h);
        });
        ro.observe(barRef.current);
        return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Apply height on mount */
    useEffect(() => {
        applyHeight();
    }, [applyHeight]);

    /* When disabled, collapse the reserved space immediately — otherwise the
       navbar (and NoticeBar, which anchors at navbar-h + tib-height) would
       leave a gap where the bar used to be. */
    // Also when there is nothing to show: no phone, no email, no newsletter
    // (a new site) — an empty dark strip above the navbar otherwise.
    const shown = enabled && !!(contact?.phone || contact?.email || newsletterEnabled);
    useEffect(() => {
        if (!shown) document.documentElement.style.setProperty('--tib-height', '0px');
        else applyHeight();
    }, [shown, applyHeight]);

    /* ── Escape key closes modal ── */
    useEffect(() => {
        if (!showModal) return;
        const h = (e) => { if (e.key === 'Escape') closeModal(); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    /* ── Newsletter subscribe ── */
    // The visible privacy notice ("By subscribing you agree…") serves as the
    // affirmative-consent statement — clicking "Subscribe Now" is the
    // unambiguous opt-in act under GDPR Art. 4(11). We send that exact text
    // in `consentText` so the audit log can later prove what was agreed to.
    const TOP_BAR_CONSENT_TEXT = 'By clicking Subscribe Now, I agree to receive marketing emails about deals and travel offers. I can unsubscribe at any time via the link in any email.';
    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setStatus('loading');
        try {
            const res = await fetch(`${PROXY_API_PATH}/newsletter/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email.trim(),
                    consent: true,
                    consentText: TOP_BAR_CONSENT_TEXT,
                }),
            });
            const json = await res.json();
            if (!json.ok) { setStatus('error'); return; }
            setStatus(json.alreadySubscribed ? 'duplicate' : 'success');
            if (!json.alreadySubscribed) setEmail('');
        } catch {
            setStatus('error');
        }
    };

    const phone     = contact?.phone || DEFAULTS.phone;
    const emailAddr = contact?.email || DEFAULTS.email;

    if (!shown) return null;

    return (
        <>
            {/* ═══ Fixed top bar ═══ */}
            <div ref={barRef} className="tib-bar tib-visible" data-i18n-zone="true" translate="no">
                <div className="tib-inner">

                    {/* All items inline */}
                    <div className="tib-contacts">
                        {phone && (
                            <a
                                href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                className="tib-item"
                            >
                                <Phone size={12} className="tib-item-icon" />
                                <span className="tib-item-text notranslate" translate="no">{phone}</span>
                            </a>
                        )}

                        {phone && emailAddr && <span className="tib-divider" />}

                        {emailAddr && (
                            <a href={`mailto:${emailAddr}`} className="tib-item tib-item-email notranslate" translate="no">
                                <Mail size={12} className="tib-item-icon" />
                                <span className="tib-item-text">{t('topBar.emailUs')}</span>
                            </a>
                        )}

                        {newsletterEnabled && (
                            <>
                                <span className="tib-divider" />
                                <button className="tib-sub-btn" onClick={openModal} aria-label={t('topBar.subscribeAria')}>
                                    <Bell size={11} />
                                    <span>{t('topBar.subscribe')}</span>
                                </button>
                            </>
                        )}
                    </div>

                </div>
            </div>

            {/* ═══ Subscribe modal ═══ */}
            {newsletterEnabled && showModal && (
                <div className="tib-overlay" onClick={closeModal} role="dialog" aria-modal="true" aria-label={t('topBar.subscribeForDealsAria')}>
                    <div className="tib-modal" onClick={e => e.stopPropagation()}>

                        {/* close */}
                        <button className="tib-modal-x" onClick={closeModal} aria-label={t('topBar.close')}>
                            <X size={17} />
                        </button>

                        {/* header */}
                        <div className="tib-modal-head">
                            <div className="tib-modal-icon-wrap">
                                <Bell size={27} />
                            </div>
                            <h2 className="tib-modal-title">{t('topBar.modalTitle')}</h2>
                            <p className="tib-modal-desc">
                                {t('topBar.modalDesc')}
                            </p>
                        </div>

                        {/* feedback after submit */}
                        {(status === 'success' || status === 'duplicate') ? (
                            <div className={`tib-feedback ${status === 'success' ? 'tib-fb-ok' : 'tib-fb-dup'}`}>
                                <CheckCircle size={22} style={{ flexShrink: 0, marginTop: 1 }} />
                                <div>
                                    <p className="tib-fb-title">
                                        {status === 'success' ? t('topBar.feedbackSuccessTitle') : t('topBar.feedbackDupTitle')}
                                    </p>
                                    <p className="tib-fb-msg">
                                        {status === 'success'
                                            ? t('topBar.feedbackSuccessMsg')
                                            : t('topBar.feedbackDupMsg')}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <form className="tib-modal-form" onSubmit={handleSubscribe}>
                                {status === 'error' && (
                                    <div className="tib-error-pill">
                                        <AlertCircle size={13} />
                                        <span>{t('topBar.error')}</span>
                                    </div>
                                )}

                                <div className="tib-input-wrap">
                                    <Mail size={15} className="tib-input-ico" />
                                    <input
                                        type="email"
                                        className="tib-email-input"
                                        placeholder={t('topBar.emailPlaceholder')}
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                        autoFocus
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="tib-submit-btn"
                                    disabled={status === 'loading'}
                                >
                                    {status === 'loading' ? (
                                        <><Loader2 size={15} className="tib-spin" />{t('topBar.subscribing')}</>
                                    ) : (
                                        <><Send size={15} />{t('topBar.subscribeNow')}</>
                                    )}
                                </button>
                            </form>
                        )}

                        <p className="tib-modal-privacy">{t('topBar.privacyNote')}</p>

                    </div>
                </div>
            )}
        </>
    );
};

export default TopInfoBar;
